import { ArrowLeft, Users, CheckCircle2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import RelatedLinks from "@/components/RelatedLinks";

const tracks = [
  {
    role: "Server-side development engineers",
    scope:
      "LLM research platform, agent framework components, R&D efficiency infrastructure, the DeepSeek API, online services and data engineering.",
  },
  {
    role: "Agent elastic computing engineers",
    scope:
      "Split between platform development and the underlying systems that let agent workloads scale up and down on demand.",
  },
];

const DeepseekHiring150Engineers = () => {
  const navigate = useNavigate();
  const canonical = "https://deepseek.ai/blog/deepseek-hiring-150-engineers-systems-shift";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek Opens ~150 Engineering Roles and Zero Research Roles: The AI Race Moves From Training to Systems",
    datePublished: "2026-09-08T16:00:00+00:00",
    dateModified: "2026-09-08T16:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "On September 8, 2026 DeepSeek opened around 150 positions for senior engineers with 2–10 years of experience — every one on the engineering side, none in AI research. Why the moat is moving from the lab to serving infrastructure, and what it means for API reliability.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek hiring 150 engineers", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How many roles did DeepSeek open?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "About 150 new positions, announced on Tuesday September 8, 2026, targeting senior engineers with two to ten years of experience.",
        },
      },
      {
        "@type": "Question",
        name: "Why are there no AI research roles?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Every one of the new roles sits on the engineering side. Analysts reading the posting see competition among large-language-model developers shifting from training models to building systems: as capability gaps between foundation models narrow, the advantage moves out of the laboratory and into how reliably and cheaply models are actually deployed.",
        },
      },
      {
        "@type": "Question",
        name: "What kinds of engineers is DeepSeek hiring?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Two tracks. Server-side development engineers work across the LLM research platform, agent framework components, R&D efficiency infrastructure, the DeepSeek API, online services and data engineering. Agent elastic computing engineers are split between platform development and underlying systems.",
        },
      },
      {
        "@type": "Question",
        name: "How is this different from DeepSeek's June recruitment drive?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The June drive was broad and included research positions. This round contains not a single AI research role — it is entirely systems and infrastructure, which is what makes it a signal rather than routine growth.",
        },
      },
      {
        "@type": "Question",
        name: "Does this affect the DeepSeek API?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Indirectly, and in the direction users want. Several tracks point straight at the API, online services and agent scaling — the areas behind capacity throttling and the announced peak-hour surcharge. It is a staffing signal, not a published roadmap commitment.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek Hires 150 Engineers — and Zero Researchers</title>
        <meta
          name="description"
          content="September 8, 2026: DeepSeek opened ~150 senior engineering roles and not one AI research role. Inside the shift from training models to building systems, and what it means for the API."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="DeepSeek Opens ~150 Engineering Roles and Zero Research Roles"
        />
        <meta
          property="og:description"
          content="Server-side development and agent elastic computing — the moat is moving from the lab to the serving stack. What DeepSeek's hiring round says about the next year of the API."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek hiring 150 engineers</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Users className="h-3.5 w-3.5" />
            AI News · September 8, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek Opens ~150 Engineering Roles — and Not One AI Research Role
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · September 8, 2026 · 5 min read
            </p>
            <p className="text-base text-slate-500 mb-8 italic">
              On Tuesday September 8, 2026, DeepSeek posted around 150 new positions aimed at senior
              engineers with two to ten years of experience. Unlike its broad June recruitment drive,
              every single role sits on the engineering side. For a lab whose reputation was built in
              the research paper, that absence is the story.
            </p>

            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The two tracks</h2>
                <div className="space-y-4">
                  {tracks.map((t) => (
                    <div key={t.role}>
                      <h3 className="font-semibold text-slate-900 text-sm mb-1">{t.role}</h3>
                      <p className="text-sm text-slate-700">{t.scope}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-600 mt-4">
                  Scope per a post by Cui Tianyi, head of DeepSeek's Harness team, as reported on
                  September 8, 2026.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Why the moat moved out of the lab
            </h2>
            <p className="text-slate-700 mb-4">
              The reading offered by analysts quoted on the announcement is blunt: competition among
              large-language-model developers is shifting from training models to building systems. As
              capability gaps between frontier foundation models narrow, being half a benchmark point
              ahead stops deciding anything. What decides things is whether the model answers in 400
              milliseconds under load, whether the agent loop survives a twenty-step task, and what a
              million served tokens actually cost you.
            </p>
            <p className="text-slate-700 mb-8">
              You can see the same shift in DeepSeek's shipping record over the last months. The
              visible output has been serving and tooling, not new base models:{" "}
              <Link to="/blog/deepseek-harness-open-source-claude-code-alternative" className="text-[#0066FF] underline">
                DeepSeek Harness
              </Link>{" "}
              as a plugin-first agent runtime,{" "}
              <Link to="/blog/inside-deepseek-dspark-lossless-inference" className="text-[#0066FF] underline">
                DSpark
              </Link>{" "}
              for faster inference at identical output, and a string of API-level changes —{" "}
              <Link to="/blog/deepseek-chat-reasoner-retired-billing-impact" className="text-[#0066FF] underline">
                retiring the legacy model aliases
              </Link>{" "}
              and repricing the tiers. Hiring 150 systems engineers is that strategy written as a
              headcount plan.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              "Agent elastic computing" is the interesting phrase
            </h2>
            <p className="text-slate-700 mb-8">
              Ordinary chat traffic is fairly predictable. Agent traffic is not: one user request can
              fan out into dozens of tool calls, long-running sessions and retry storms, and the load
              it produces is spiky in a way that classic autoscaling handles badly. A dedicated track
              for elastic compute under agent workloads says DeepSeek expects agents — not chat — to
              be the load it must engineer for. That aligns with a{" "}
              <Link to="/blog/deepseek-huawei-ascend-160000-chip-cluster" className="text-[#0066FF] underline">
                gigawatt-scale inference build-out
              </Link>{" "}
              and with agent scores being the metric it now advertises.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What it means if you build on the API
            </h2>
            <ul className="text-slate-700 space-y-3 list-disc pl-6 mb-8">
              <li>
                <strong>Reliability work is being staffed.</strong> The API, online services and
                agent scaling are named explicitly — the exact surfaces behind throttling complaints.
              </li>
              <li>
                <strong>Expect more platform changes, fewer surprise base models.</strong> A systems
                hiring round points to endpoints, tooling and pricing structure moving faster than
                model names.
              </li>
              <li>
                <strong>Pin your model strings and read changelogs.</strong> DeepSeek ships
                platform-level changes with short notice; the{" "}
                <Link to="/blog/deepseek-v41-flash-beta-native-multimodal" className="text-[#0066FF] underline">
                  V4.1 Flash test build with an expiry date in its own name
                </Link>{" "}
                is the current example.
              </li>
              <li>
                <strong>Budget from the official rate card.</strong> Our{" "}
                <Link to="/pricing" className="text-[#0066FF] underline">
                  pricing page
                </Link>{" "}
                tracks it weekly, including the status of the announced peak-hour surcharge.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-5 mb-10">
              {faqSchema.mainEntity.map((q) => (
                <div key={q.name}>
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#0066FF] mt-1 shrink-0" />
                    {q.name}
                  </h3>
                  <p className="text-slate-700 text-sm pl-6">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Keep reading</h2>
              <RelatedLinks />
            </div>
          </article>
        </main>
      </div>
    </>
  );
};

export default DeepseekHiring150Engineers;
